import React, { useState, useEffect } from 'react';
import { 
    Calendar, Clock, MapPin, User, Info, CheckCircle, ExternalLink, X, Plus, Mail, Building
} from 'lucide-react';
import { Drawer, Box, IconButton, Typography } from '@mui/material';
import axios from 'axios';
import API_ENDPOINTS from '../../../config/apiEndpoints';
import { LeadDetails as CommonLeadDetails } from '../../../components/common/LeadDetails/LeadDetails';
import LeadActionOperation from '../../../common/LeadActionOperation/LeadActionOperation';
import Loader from '../../../components/Loader/Loader';
import './FollowUpDetails.css';
import { getInitials } from '../../../utils/helpers';

const FollowUpDetails = ({ followup, onClose, isInline, onRefresh, scorecardData, initialMode = 'view', crmOperationPerms }) => {
    const [mode, setMode] = useState(initialMode); // 'view', 'lead-profile', 'interaction-history', etc.
    const [interactionHistory, setInteractionHistory] = useState([]);
    const [interactionHistoryMode, setInteractionHistoryMode] = useState(initialMode === 'interaction-history');
    const [loadingHistory, setLoadingHistory] = useState(false);

    // Initializations
    useEffect(() => {
        if (!followup) return;
        setMode(initialMode);
        setInteractionHistoryMode(initialMode === 'interaction-history');
        fetchInteractionHistory();
    }, [followup, initialMode]);

    const fetchInteractionHistory = async () => {
        if (!followup?.LeadId && !followup?.enquiryId && !followup?.id) return;
        setLoadingHistory(true);
        try {
            const leadId = followup.LeadId || followup.enquiryId || followup.id;
            const NEWV3_BASE_URL = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');
            const url = `${NEWV3_BASE_URL}${API_ENDPOINTS.APPOINTMENTS.GET_INTERACTION_HISTORY}`;
            console.log("Calling Interaction History URL:", url);
            
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    Token: "-2295521862261168",
                    Details: {
                        LeadNo: parseInt(leadId, 10),
                        ParentID: parseInt(localStorage.getItem('UserId') || localStorage.getItem('userId') || '34594', 10)
                    }
                })
            });
            const result = await response.json();
            const data = result.d || result;
            setInteractionHistory(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Error fetching interaction history:", error);
        } finally {
            setLoadingHistory(false);
        }
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr || dateStr === 'No Date' || dateStr.startsWith('1900')) return '--';
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return '--';
        return date.toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    const getStatusClass = (dateStr) => {
        if (!dateStr) return 'no-followup';
        const dueDate = new Date(dateStr);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        dueDate.setHours(0, 0, 0, 0);

        if (dueDate < today) return 'overdue';
        if (dueDate.getTime() === today.getTime()) return 'due-today';
        return 'scheduled';
    };

    const getDaysDiff = (dateStr) => {
        if (!dateStr) return 'Upcoming';
        const dueDate = new Date(dateStr);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        dueDate.setHours(0, 0, 0, 0);

        const diffTime = dueDate.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 0) return `${Math.abs(diffDays)} Days Overdue`;
        if (diffDays === 0) return 'Due Today';
        return `Due in ${diffDays} Days`;
    };


    if (!followup) return null;

    const content = (
        <div className={`details-panel-new ${isInline ? 'inline-panel' : ''}`} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {!isInline && (mode === 'view' || interactionHistoryMode) && (
                <div className="premium-task-details-header">
                    <button className="task-details-close-btn" onClick={onClose} title="Close">
                        <X size={20} />
                    </button>
                    <div className="header-title-container">
                        <h2 className="task-details-header-title">Follow-up Details</h2>
                    </div>
                    <div className="task-details-header-actions">
                    </div>
                </div>
            )}

            <div className={`details-body ${mode === "lead-profile" ? "lead-profile-body" : ""}`} style={{ flex: 1, overflowY: 'auto' }}>
                {mode === "view" && !interactionHistoryMode && (
                    <div className="premium-details-container" style={{ padding: '20px' }}>
                        <div className="premium-details-card">
                            <div className="card-body">
                                <div className="card-top-row">
                                    <h1 className="premium-task-title">
                                        {followup.followupstatus || followup.FollowupStatus || "Follow-up"} {formatDateTime(followup.followupdate || followup.FollowupDate)}
                                    </h1>
                                    <div className={`status-badge-inline ${getStatusClass(followup.followupdate || followup.FollowupDate)}`} style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold' }}>
                                        {getDaysDiff(followup.followupdate || followup.FollowupDate)}
                                    </div>
                                </div>

                                <div className="related-to-premium">
                                    <div className="related-initials-avatar" onClick={() => setMode('lead-profile')} style={{ cursor: 'pointer' }}>
                                        {getInitials(followup.personname || followup.PersonName)}
                                    </div>
                                    <div className="related-label-box">
                                        <span className="label">Related To:</span>
                                        <span className="value-link" onClick={() => setMode('lead-profile')}>
                                            <ExternalLink size={14} style={{ marginRight: '4px' }} />
                                            {followup.personname || followup.PersonName || 'Unknown Lead'}
                                        </span>
                                    </div>
                                </div>

                                <div className="metadata-grid-premium">
                                    {[
                                        { label: 'Due Date:', value: (followup.followupdate || followup.FollowupDate) ? formatDateTime(followup.followupdate || followup.FollowupDate) : '--' },
                                        { label: 'Created On:', value: formatDateTime(followup.createdon || followup.CreatedOn) },
                                        { label: 'Source:', value: scorecardData?.source || followup.source || followup.Source || '--' },
                                        { label: 'Amount Paid:', value: (followup.AmountPaid || followup.amountpaid) ? `₹ ${followup.AmountPaid || followup.amountpaid}` : '₹ 0.00' }
                                    ].map((meta, i) => (
                                        <div key={i} className="metadata-row">
                                            <span className="metadata-label">{meta.label}</span>
                                            <span className="metadata-value">{meta.value}</span>
                                        </div>
                                    ))}
                                </div>

                                <div className="premium-description-section">
                                    <div className="description-premium-area">
                                        <span className="label">DESCRIPTION:</span>
                                        <p className="text">{followup.remark || followup.Remarks || '--'}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {mode === 'lead-profile' ? (
                    <CommonLeadDetails
                        leadId={followup?.LeadId || followup?.enquiryId || followup?.id}
                        module="followup"
                        onBack={() => setMode('view')}
                    />
                ) : interactionHistoryMode ? (
                    <div className="interaction-history-container" style={{ padding: '20px', background: '#e2e8f0', minHeight: '100%' }}>
                        <div className="history-card-v3" style={{ background: 'white', padding: '0 20px 20px 20px', borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                            <div className="history-header-v3" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', borderBottom: '1px solid #e2e8f0' }}>
                                <h3 style={{ margin: 0, fontSize: '15px', color: '#1e293b', fontWeight: '600' }}>Interaction History</h3>
                                <button className="add-history-btn" onClick={() => {
                                    setInteractionHistoryMode(false);
                                    setMode('add_followup_premium');
                                }} style={{ background: 'white', border: '1px solid #334155', color: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>
                                    <Plus size={16} />
                                </button>
                            </div>

                            {interactionHistory.length > 0 && (
                                <div className="history-lead-info-v3" style={{ display: 'flex', alignItems: 'center', padding: '16px 0', borderBottom: '1px solid #e2e8f0', gap: '16px' }}>
                                    <div 
                                        className="lead-avatar-v3" 
                                        style={{ 
                                            width: '42px', 
                                            height: '42px', 
                                            borderRadius: '50%', 
                                            background: '#fff1f2', 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center', 
                                            color: '#e11d48', 
                                            fontSize: '14px', 
                                            fontWeight: '700',
                                            flexShrink: 0,
                                            position: 'relative',
                                            border: '1px solid #ffe4e6'
                                        }}
                                    >
                                        {getInitials(interactionHistory[0].PersonName)}
                                    </div>
                                    <div className="lead-text-v3">
                                        <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#1e293b', fontWeight: 'bold' }}>{interactionHistory[0].PersonName}</h4>
                                        <div className="lead-meta-row-v3" style={{ display: 'flex', gap: '20px', fontSize: '13px', color: '#475569' }}>
                                            <span><strong style={{ color: '#1e293b', fontWeight: '500' }}>Mobile No. :</strong> {interactionHistory[0].MobileNo || '--'}</span>
                                            <span><strong style={{ color: '#1e293b', fontWeight: '500' }}>Lead No. :</strong> {interactionHistory[0].LeadNo || '--'}</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="history-list-v3" style={{ paddingTop: '16px' }}>
                                {loadingHistory ? (
                                    <Loader message="Loading history..." />
                                ) : interactionHistory.length > 0 ? (
                                    interactionHistory.map((interaction, index) => (
                                        <div key={index} className="history-item-v3" style={{ paddingBottom: '16px', marginBottom: '16px', borderBottom: index < interactionHistory.length - 1 ? '1px solid #e2e8f0' : 'none' }}>
                                            <div className="item-status-v3" style={{ fontWeight: 'bold', color: '#1e293b', marginBottom: '16px', fontSize: '14px' }}>{interaction.FollowupStatus}</div>
                                            {interaction.Remarks && <div className="item-remarks-v3" style={{ marginBottom: '16px', color: '#475569', fontSize: '13px' }}>{interaction.Remarks}</div>}

                                            <div className="item-details-grid-v3" style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#475569', gap: '20px' }}>
                                                <div className="detail-col" style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '220px' }}>
                                                    <div className="detail-row" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <span className="label" style={{ fontWeight: '600', color: '#1e293b', display: 'inline-block', width: '100px', flexShrink: 0 }}>Created Date:</span>
                                                        <span className="value" style={{ whiteSpace: 'nowrap' }}>{interaction.CreatedOn}</span>
                                                    </div>
                                                    <div className="detail-row" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <span className="label" style={{ fontWeight: '600', color: '#1e293b', display: 'inline-block', width: '100px', flexShrink: 0 }}>Due Date:</span>
                                                        <span className="value" style={{ whiteSpace: 'nowrap' }}>{interaction.NextStatusDate || interaction.NextstatusDate}</span>
                                                    </div>
                                                    <div className="detail-row" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <span className="label" style={{ fontWeight: '600', color: '#1e293b', display: 'inline-block', width: '100px', flexShrink: 0 }}>Amount:</span>
                                                        <span className="value">{interaction.AmountPaid}</span>
                                                    </div>
                                                </div>
                                                <div className="detail-col" style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                                                    <div className="detail-row" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <span className="label" style={{ fontWeight: '600', color: '#1e293b', display: 'inline-block', width: '80px', flexShrink: 0 }}>Assign To:</span>
                                                        <span className="value" style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#475569', whiteSpace: 'nowrap' }}>
                                                            <User size={18} style={{ color: '#64748b' }} />
                                                            {interaction.AssignedTo}
                                                        </span>
                                                    </div>
                                                    {interaction.CreatedBy && interaction.CreatedBy !== 'System' && (
                                                        <div className="detail-row" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <span className="label" style={{ fontWeight: '600', color: '#1e293b', display: 'inline-block', width: '80px', flexShrink: 0 }}>Created By:</span>
                                                            <span className="value" style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#475569', whiteSpace: 'nowrap' }}>
                                                                <User size={18} style={{ color: '#64748b' }} />
                                                                {interaction.CreatedBy}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10, opacity: 0.8 }}>
                                        <img src="/nodata.gif" alt="No History Found" style={{ width: '180px', marginBottom: '16px' }} />
                                        <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#64748b' }}>No interaction history found.</Typography>
                                    </Box>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="centralized-action-container">
                        <LeadActionOperation
                            crmOperationPerms={crmOperationPerms}
                            mode={
                                (mode === 'email' || mode === 'send-mail') ? 'mail' :
                                (mode === 'add_followup_premium' || mode === 'add-followup-premium') ? 'followup-premium' :
                                (mode === 'add_followup' || mode === 'add-followup') ? 'followup' :
                                (mode === 'add-notes' || mode === 'add-note') ? 'add-notes' :
                                (mode === 'fill-webform' || mode === 'fillWebform') ? 'fill-webform' :
                                (mode === 'upload-document' || mode === 'upload') ? 'upload-document' :
                                (mode === 'sms' || mode === 'send-sms') ? 'sms' :
                                (mode === 'add-task' || mode === 'addtask') ? 'add-task' :
                                (mode === 'add-deal' || mode === 'adddeal') ? 'add-deal' :
                                (mode === 'add-appointment' || mode === 'addappointment') ? 'add-appointment' :
                                (mode === 'send-voice' || mode === 'voice') ? 'send-voice' :
                                mode
                            }
                            entity={{
                                ...followup,
                                // Standardize ID fields for LeadActionOperation fetch logic
                                enquiryId: followup.LeadId || followup.enquiryId || followup.id,
                                leadId: followup.LeadId || followup.enquiryId || followup.id,
                                id: followup.LeadId || followup.enquiryId || followup.id,
                                // Pass through real-time contact data from interaction history if available
                                MobileNo: interactionHistory[0]?.MobileNo || followup.MobileNo || "",
                                Email: interactionHistory[0]?.EmailID || followup.EmailID || "",
                                Phones: interactionHistory[0]?.MobileNo1 || interactionHistory[0]?.MobileNo2 || "",
                                Emails: interactionHistory[0]?.EmailID1 || interactionHistory[0]?.EmailID2 || ""
                            }}
                            onBack={() => {
                                setInteractionHistoryMode(true);
                                setMode('view');
                            }}
                            onSuccess={() => {
                                setMode('view');
                                if (onClose) onClose();
                                if (onRefresh) onRefresh();
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );

    if (isInline) {
        return content;
    }

    return (
        <Drawer
            anchor="right"
            open={true}
            onClose={onClose}
            sx={{ zIndex: 1000000 }} // Ensure it's above EVERYTHING, including sticky headers
            PaperProps={{
                sx: {
                    width: { xs: '100vw', sm: '80vw', md: '70vw', lg: '800px' },
                    maxWidth: '100%',
                    height: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    p: 0,
                    borderLeft: '1px solid rgba(0, 0, 0, 0.12)',
                    boxShadow: '-4px 0 15px rgba(0, 0, 0, 0.1)'
                }
            }}
        >
            <Box sx={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#f8fafc' }}>
                {content}
            </Box>
        </Drawer>
    );
};

export default FollowUpDetails;
